import log from '@/lib/logger';
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Input } from '@/components/tailwind/input'
import { Textarea } from '@/components/tailwind/textarea'
import { Switch } from '@/components/tailwind/switch'
import { Button } from '@/components/tailwind/button'
import { Field, Label, FieldGroup } from '@/components/tailwind/fieldset'
import { Text } from '@/components/tailwind/text'
import { MembershipFeeType, CreateMembershipFeeTypeRequest, UpdateMembershipFeeTypeRequest } from '@/models/membership-fee'
import * as membershipFeeService from '@/services/membership-fee-services'
import { useAuth } from '@/context/use-auth'
import { useAdminTranslation, useCommonTranslation } from '@/i18n/hooks'
import { parseCurrencyInput, isValidCurrencyInput } from '@/lib/utils'
import ValidationAlert from '@/components/validation-alert'

// Will be replaced with translations from i18n
const createSchema = (t: any) => z.object({
    name: z.string().min(1, t('adminSettings.membershipFees.form.validation.nameRequired')),
    description: z.string().optional(),
    isDefault: z.boolean(),
    initialAmount: z.string()
        .min(1, t('adminSettings.membershipFees.form.validation.amountRequired'))
        .refine(isValidCurrencyInput, t('adminSettings.membershipFees.form.validation.amountPositive')),
    validFrom: z.string()
        .min(1, t('adminSettings.membershipFees.form.validation.validFromRequired'))
        .refine(
            (date) => {
                const selectedDate = new Date(date)
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                return selectedDate >= today
            },
            { message: t('adminSettings.membershipFees.form.validation.dateNotInPast') }
        )
})

const updateSchema = (t: any) => z.object({
    name: z.string().min(1, t('adminSettings.membershipFees.form.validation.nameRequired')),
    description: z.string().optional(),
    isDefault: z.boolean(),
    newAmount: z.string()
        .refine((val) => val === '' || isValidCurrencyInput(val), t('adminSettings.membershipFees.form.validation.amountPositive'))
        .optional()
        .or(z.literal('')),
    newAmountValidFrom: z.string().optional()
}).refine(
    (data) => {
        if (data.newAmountValidFrom && data.newAmountValidFrom !== '') {
            const selectedDate = new Date(data.newAmountValidFrom)
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return selectedDate >= today
        }
        return true
    },
    {
        message: t('adminSettings.membershipFees.form.validation.dateNotInPast'),
        path: ['newAmountValidFrom']
    }
)

interface MembershipFeeFormProps {
    feeType: MembershipFeeType | null
    onSuccess: () => void
    onCancel: () => void
    hasDefaultFeeType?: boolean
}

export function MembershipFeeForm({ feeType, onSuccess, onCancel, hasDefaultFeeType = true }: MembershipFeeFormProps) {
    const { t } = useAdminTranslation()
    const { t: tCommon } = useCommonTranslation()
    const { token } = useAuth()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    
    const isEditing = !!feeType
    const schema = isEditing ? updateSchema(t) : createSchema(t)
    
    const form = useForm<any>({
        resolver: zodResolver(schema as any),
        defaultValues: isEditing ? {
            name: feeType.name,
            description: feeType.description || '',
            isDefault: feeType.isDefault,
            newAmount: feeType.currentAmount ? feeType.currentAmount.toString().replace('.', ',') : '',
            newAmountValidFrom: new Date().toISOString().split('T')[0]
        } : {
            name: '',
            description: '',
            isDefault: !hasDefaultFeeType,
            initialAmount: '',
            validFrom: new Date().toISOString().split('T')[0]
        }
    })
    
    async function onSubmit(data: any) {
        if (!token) {
            setError('Keine Authentifizierung vorhanden')
            return
        }
        
        setLoading(true)
        setError(null)
        
        try {
            if (isEditing) {
                // Check if amount actually changed
                const newAmountValue = data.newAmount ? parseCurrencyInput(data.newAmount) : undefined
                const currentAmountValue = feeType.currentAmount || 0
                const amountChanged = newAmountValue !== undefined && newAmountValue !== currentAmountValue
                
                const updateData: UpdateMembershipFeeTypeRequest = {
                    name: data.name,
                    description: data.description || undefined,
                    isDefault: data.isDefault,
                    newAmount: amountChanged ? newAmountValue : undefined,
                    newAmountValidFrom: amountChanged ? data.newAmountValidFrom : undefined
                }
                
                // Show info if nothing changed
                if (!amountChanged && 
                    data.name === feeType.name && 
                    data.description === (feeType.description || '') && 
                    data.isDefault === feeType.isDefault) {
                    setError(t('adminSettings.membershipFees.form.validation.noChanges'))
                    setLoading(false)
                    return
                }
                
                await membershipFeeService.updateMembershipFeeType(token, feeType.id, updateData)
            } else {
                const parsedAmount = parseCurrencyInput(data.initialAmount)
                const createData: CreateMembershipFeeTypeRequest = {
                    name: data.name,
                    description: data.description || undefined,
                    isDefault: data.isDefault,
                    initialAmount: parsedAmount,
                    validFrom: data.validFrom
                }
                await membershipFeeService.createMembershipFeeType(token, createData)
            }
            onSuccess()
        } catch (err: any) {
            log.error('Error saving membership fee type:', err)
            setError(err.message || t('adminSettings.membershipFees.toast.error.description'))
        } finally {
            setLoading(false)
        }
    }
    
    const minDate = new Date().toISOString().split('T')[0]
    
    return (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
            {error && (
                <div className="bg-red-50 dark:bg-red-950 border-l-4 border-red-400 dark:border-red-500 p-4">
                    <Text className="text-red-700 dark:text-red-300">{error}</Text>
                </div>
            )}
            
            <FieldGroup>
                <Field>
                    <Label>{t('adminSettings.membershipFees.form.fields.name')}</Label>
                    <Input
                        {...form.register('name')}
                        invalid={!!form.formState.errors.name}
                        data-testid="membership-fee-name"
                    />
                    {form.formState.errors.name && (
                        <Text className="text-red-500 text-sm mt-1" data-testid="membership-fee-name-error">
                            {String(form.formState.errors.name?.message || '')}
                        </Text>
                    )}
                </Field>
                
                <Field>
                    <Label>{t('adminSettings.membershipFees.form.fields.description')}</Label>
                    <Textarea
                        {...form.register('description')}
                        rows={3}
                        placeholder={t('adminSettings.membershipFees.form.fields.descriptionPlaceholder')}
                        data-testid="membership-fee-description"
                    />
                </Field>
                
                <Field className="mt-6">
                    <div className="grid grid-cols-[200px_1fr] gap-4 items-center">
                        <Label>{t('adminSettings.membershipFees.form.fields.isDefault')}</Label>
                        <Switch
                            {...form.register('isDefault')}
                            checked={form.watch('isDefault')}
                            onChange={(checked) => form.setValue('isDefault', checked)}
                            data-testid="membership-fee-is-default"
                        />
                    </div>
                </Field>
                
                {!isEditing && (
                    <>
                        <Field>
                            <div className="grid grid-cols-[200px_1fr] gap-4 items-start">
                                <Label className="pt-2">{t('adminSettings.membershipFees.form.fields.initialAmount')}</Label>
                                <div className="space-y-1">
                                    <div className="relative w-40">
                                        <Input
                                            type="text"
                                            {...form.register('initialAmount')}
                                            invalid={!!form.formState.errors.initialAmount}
                                            placeholder="0,00"
                                            data-testid="membership-fee-initial-amount"
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <span className="text-gray-500 dark:text-gray-400 text-sm">€</span>
                                        </div>
                                    </div>
                                    {form.formState.errors.initialAmount && (
                                        <ValidationAlert message={String(form.formState.errors.initialAmount?.message || '')} className="!mb-0 !mt-1" />
                                    )}
                                </div>
                            </div>
                        </Field>
                        
                        <Field>
                            <div className="grid grid-cols-[200px_1fr] gap-4 items-start">
                                <Label className="pt-2">{t('adminSettings.membershipFees.form.fields.validFrom')}</Label>
                                <div className="space-y-1">
                                    <Input
                                        type="date"
                                        min={minDate}
                                        {...form.register('validFrom')}
                                        invalid={!!form.formState.errors.validFrom}
                                        className="!w-40"
                                        data-testid="membership-fee-valid-from"
                                    />
                                    {form.formState.errors.validFrom && (
                                        <Text className="text-red-500 text-sm">
                                            {String(form.formState.errors.validFrom?.message || '')}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </Field>
                    </>
                )}
                
                {isEditing && (
                    <>
                        <Field>
                            <div className="grid grid-cols-[200px_1fr] gap-4 items-start">
                                <Label className="pt-2">{t('adminSettings.membershipFees.form.fields.newAmount')}</Label>
                                <div className="space-y-1">
                                    <div className="relative w-40">
                                        <Input
                                            type="text"
                                            {...form.register('newAmount')}
                                            invalid={!!form.formState.errors.newAmount}
                                            placeholder="0,00"
                                            data-testid="membership-fee-new-amount"
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <span className="text-gray-500 dark:text-gray-400 text-sm">€</span>
                                        </div>
                                    </div>
                                    {form.formState.errors.newAmount && (
                                        <ValidationAlert message={String(form.formState.errors.newAmount?.message || '')} className="!mb-0 !mt-1" />
                                    )}
                                </div>
                            </div>
                        </Field>
                        
                        <Field>
                            <div className="grid grid-cols-[200px_1fr] gap-4 items-start">
                                <Label className="pt-2">{t('adminSettings.membershipFees.form.fields.newAmountValidFrom')}</Label>
                                <div className="space-y-1">
                                    <Input
                                        type="date"
                                        min={minDate}
                                        {...form.register('newAmountValidFrom')}
                                        invalid={!!form.formState.errors.newAmountValidFrom}
                                        className="!w-40"
                                        data-testid="membership-fee-new-amount-valid-from"
                                    />
                                    {form.formState.errors.newAmountValidFrom && (
                                        <Text className="text-red-500 text-sm">
                                            {String(form.formState.errors.newAmountValidFrom?.message || '')}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </Field>
                    </>
                )}
            </FieldGroup>
            
            <div className="flex justify-end gap-3">
                <Button type="button" plain onClick={onCancel} disabled={loading} data-testid="membership-fee-cancel-button">
                    {tCommon('buttons.cancel.text')}
                </Button>
                <Button type="submit" color="zinc" disabled={loading} data-testid="membership-fee-save-button">
                    {loading ? tCommon('saving') : (isEditing ? t('adminSettings.membershipFees.save') : t('adminSettings.membershipFees.create'))}
                </Button>
            </div>
        </form>
    )
}